import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

import { TFeaturedPeopleForm } from '../types';

import { featuredPeopleQueryKeys } from './query-keys';

export type TUpdatePeopleImagesParams = {
  id: string;
  main_logo?: File | string;
  logos?: File[] | string[];
};

export function hasImagesToUpload(
  params: Pick<TUpdatePeopleImagesParams, 'main_logo' | 'logos'>
): boolean {
  return params.main_logo instanceof File || !!params.logos?.some(logo => logo instanceof File);
}

export async function updatePeopleImages(
  params: TUpdatePeopleImagesParams
): Promise<TFeaturedPeopleForm> {
  const formData = new FormData();

  if (params.main_logo instanceof File) {
    formData.append('main_logo', params.main_logo);
  }
  if (params.logos) {
    params.logos
      .filter(logo => logo instanceof File)
      .forEach((logo, index) => {
        formData.append(`logo${index}`, logo);
      });
  }

  return mediaManagerClient.patch(`/api/v1/peoples/${params.id}/images`, formData);
}

type MutationFn = typeof updatePeopleImages;

type TUseUpdatePeopleImagesParams = {
  config?: MutationConfig<MutationFn>;
};

export function useUpdatePeopleImages({ config = {} }: TUseUpdatePeopleImagesParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: updatePeopleImages,
    onError(error) {
      addErrorNotification(t('No se ha podido actualizar la imagen'));
      console.error(error);
    },
    onSuccess() {
      addSuccessNotification(t('Se ha actualizado la imagen'));
      queryClient.invalidateQueries({ queryKey: featuredPeopleQueryKeys.all });
    },
  });
}
